import { useCallback, useEffect, useState } from 'preact/hooks'
import { dashboardApi } from '@/api/dashboard'
import type { DashboardRange, DashboardTilesDto, DashboardTileSectionsDto } from '@/types/dashboard'

interface UseDashboardTilesOptions {
  workspaceId: string
  range: DashboardRange
  /** Narrows to one campaign; omit for the workspace dashboard. */
  campaignId?: string
  limit?: number
  mock?: boolean
}

export interface UseDashboardTilesResult {
  tiles: DashboardTilesDto | null
  sections: DashboardTileSectionsDto | null
  /** First load only — the one time there is nothing on screen to keep. */
  loading: boolean
  /** A later load, with the previous payload still rendered underneath. */
  refreshing: boolean
  error: string | null
  /** Re-runs the request without a page reload, which would lose the selected range. */
  refresh: () => void
  /**
   * True when this section has no real result — the server could not compute it, or the whole
   * request failed. Distinct from an empty section, which is a real result, so the caller can show
   * an error rather than a zero.
   */
  sectionFailed: (section: keyof DashboardTileSectionsDto) => boolean
}

/**
 * Loads the whole dashboard in one request.
 *
 * Replaces a per-tile fetch in every tile component. Beyond the request count, the tiles are all
 * projections of the same server-side read, so fetching them separately let them disagree with
 * each other; one payload is one consistent snapshot.
 */
export function useDashboardTiles({
  workspaceId,
  range,
  campaignId,
  limit,
  mock = false,
}: UseDashboardTilesOptions): UseDashboardTilesResult {
  const [tiles, setTiles] = useState<DashboardTilesDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const refresh = useCallback(() => setReloadToken(token => token + 1), [])

  useEffect(() => {
    // Without a workspace there is nothing to wait for. Leaving `loading` set here left every tile
    // shimmering indefinitely while the analytics tiles beside them correctly showed their empty
    // state, so the two halves of one grid disagreed about whether the page was still loading.
    //
    // The payload is dropped too. A workspace switch can pass through an empty id, and keeping the
    // previous tiles would render one workspace's numbers under another's heading.
    if (!workspaceId) {
      setLoading(false)
      setRefreshing(false)
      setTiles(null)
      setError(null)
      return
    }

    let cancelled = false

    const load = async () => {
      // Only the first load has nothing to keep on screen. A range change re-reads with a payload
      // already rendered, so it dims rather than dissolving the whole page into skeletons.
      setTiles(current => {
        if (current) setRefreshing(true)
        else setLoading(true)
        return current
      })
      setError(null)

      const response = await dashboardApi.getTiles(workspaceId, range, { campaignId, limit, mock })
      if (cancelled) return

      if (response.error) {
        setError(response.error)
        setTiles(null)
      } else {
        setTiles(response.data ?? null)
      }
      setLoading(false)
      setRefreshing(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [workspaceId, range, campaignId, limit, mock, reloadToken])

  const failed = new Set(tiles?.Sections?.FailedSections ?? [])

  return {
    tiles,
    sections: tiles?.Sections ?? null,
    loading,
    refreshing,
    error,
    refresh,
    // A failed request computed nothing, so every section is failed rather than empty. Without this
    // the tiles fall through to their empty states and read as measurements — "No leads yet" for a
    // request that never landed.
    sectionFailed: section => error !== null || failed.has(section as string),
  }
}
